<template>
  <div class="flex flex-col w-full">
    <!-- Skeleton placeholder while search client loads -->
    <div v-if="!client" class="bg-white w-full py-4 shadow-lg">
      <div class="relative container flex lg:block">
        <div class="relative mx-auto max-w-2xl grow">
          <div class="fluid-base border-2 w-full bg-putty rounded-full pl-12 h-12 border-transparent pr-4"></div>
        </div>
      </div>
    </div>

    <AisInstantSearch v-else :search-client="client" :index-name="indexName" :stalled-search-delay="300">
      <AisConfigure :hits-per-page.camel="40" v-bind="searchParameters" />
      <div
        v-show="showBackdrop && isOpen && searchQuery.length > 0"
        class="absolute inset-x-0 bottom-0 h-screen bg-blackly/75 top-full backdrop-blur-xs"
        @click="close"
        aria-hidden="true"
      ></div>
      <div class="bg-white w-full py-4 shadow-lg">
        <AisSearchBox v-slot="{ refine }">
          <div class="relative container flex lg:block">
            <form class="relative mx-auto max-w-2xl grow" @submit.prevent="handleSubmit()" role="search">
              <label :for="id" class="sr-only">{{ $t('search.inputPlaceholder') }}</label>
              <input
                ref="searchInput"
                :id="id"
                type="search"
                v-model="searchQuery"
                :placeholder="$t('search.inputPlaceholder')"
                class="fluid-base focus:text-blackly search-input border-2 focus:bg-white w-full bg-putty rounded-full pl-12 h-12 border-transparent focus:border-br-green pr-4 leading-tightest font-medium flex items-center"
                :disabled="!client"
                aria-autocomplete="list"
                aria-controls="search-results"
                :aria-expanded="isOpen && searchQuery.length > 1"
                @input="
                  ($event) => {
                    const val = $event.currentTarget.value;
                    debouncedSearch(val, refine);
                  }
                "
              />
              <button
                class="absolute top-0 left-0 flex items-center justify-center w-12 h-full pl-2 search-button text-br-green"
                type="submit"
                :aria-label="$t('search.aria.searchInput')"
              >
                <SearchIcon class="w-6 h-6" />
              </button>
              <button
                v-if="searchQuery.length > 0"
                class="absolute top-0 right-0 h-full mr-2 w-10 flex items-center justify-center text-gray-light"
                type="button"
                :aria-label="$t('search.aria.clearSearch')"
                @click="onBackspaceClick(() => refine(''), $event)"
              >
                <BackspaceIcon class="h-6" />
              </button>
              <button
                v-else
                class="absolute sm:hidden top-0 right-0 h-full mr-2 w-10 flex items-center justify-center text-gray-light"
                :aria-label="$t('search.aria.closeSearch')"
                @click="hide()"
                :title="$t('common.close')"
              >
                <CloseIcon class="h-6" />
              </button>
            </form>
            <div v-if="searchQuery.length > 0" class="hidden mt-6 text-center fluid-xs leading-tightest lg:block">
              {{ $t('search.press') }} <strong>{{ $t('search.enter') }}</strong> {{ $t('search.to') }}
              <nuxt-link
                :to="{ path: $localeRootPath('search'), query: { query: searchQuery } }"
                class="underline"
                @click.native="close"
                >{{ $t('search.viewAllResults') }}</nuxt-link
              >
            </div>
            <button
              v-if="showCloseButton"
              class="ml-2 md:absolute right-0 top-0 w-8 h-12 flex items-center justify-center"
              :aria-label="$t('search.aria.closeSearch')"
              @click="close"
              :title="$t('controls.close')"
            >
              <IconClose class="w-4 h-4" />
            </button>
          </div>
        </AisSearchBox>
      </div>
      <AisStateResults v-if="searchQuery.length > 1 && isOpen" v-slot="{ state: { query }, results: { hits } }">
        <div class="absolute inset-x-0 top-full" style="height: calc(100vh - 328px)">
          <div aria-live="polite" class="sr-only">
            {{
              hits && hits.length ? $t('search.aria.resultsFound', { count: hits.length }) : $t('search.aria.noResults')
            }}
          </div>
          <SearchModalResults
            v-if="hits"
            ref="searchModalResults"
            id="search-results"
            class="grow overflow-y-auto shadow-xl bg-white h-full"
            :query="query"
            :hits="hits"
            role="listbox"
            aria-live="polite"
            aria-labelledby="search-input"
            @clicked="close"
          />
        </div>
      </AisStateResults>
    </AisInstantSearch>
  </div>
</template>

<script setup>
import { searchClient } from '@algolia/client-search';
import { AisInstantSearch, AisSearchBox, AisConfigure, AisStateResults } from 'vue-instantsearch/vue3/es';
import SearchModalResults from '@/components/layout/header/SearchModalResults';
import SearchIcon from '@/components/icons/SearchIcon';
import IconClose from '@/components/layout/header/IconClose';
import BackspaceIcon from '@/components/icons/BackspaceIcon';
import CloseIcon from '@/components/icons/CloseIcon';
import { debounce } from 'lodash';
import { useSearchStore } from '@/stores/search';
import { useRouter } from 'vue-router';

const props = defineProps({
  showBackdrop: {
    type: Boolean,
    default: true,
  },
  showCloseButton: {
    type: Boolean,
    default: true,
  },
  isInModal: {
    type: Boolean,
    default: true,
  },
  id: {
    type: String,
    default: 'search-input',
  },
});

const { $localeRootPath } = useNuxtApp();
const searchStore = useSearchStore();
const router = useRouter();
const config = useRuntimeConfig();

const searchInput = ref(null);
const searchModalResults = ref(null);
const client = ref(null);
const sectionGroups = ref(['Products', 'Exercises', 'Knowledge', 'Story', 'Service']);
const searchQuery = ref(searchStore.searchQuery || '');
let debouncedSearch = null;

// Store getters
const indexName = computed(() => searchStore.indexName);
const isOpen = computed(() => searchStore.isOpen);

// Store actions
const close = () => searchStore.close();
const setQuery = (query) => searchStore.setQuery(query);
const setIndexName = () => searchStore.setIndexName();
const hide = () => searchStore.hide();

const searchParameters = computed(() => {
  return {
    filters: sectionGroups.value.map((g) => `sectionGroup:${g}`).join(' OR '),
  };
});

onMounted(() => {
  if (!client.value) setupSearch();

  debouncedSearch = debounce((val, refine) => {
    refine(val);
    setQuery(val);
  }, 300);
});

function setupSearch() {
  setIndexName();
  const algoliaClient = searchClient(config.public.algoliaApplicationId, config.public.algoliaSearchApiKey);

  client.value = {
    search(requests) {
      if (requests[0].params.query === '') {
        return Promise.resolve({ results: [{}] });
      }

      return algoliaClient.search(requests);
    },
  };
}

function focusSearchInput() {
  nextTick(() => {
    if (searchInput.value) {
      searchInput.value.focus();
    }
  });
}

function handleSubmit() {
  debouncedSearch.flush();

  if (searchQuery.value.length > 0) {
    setQuery(searchQuery.value);

    router.push({
      path: $localeRootPath('/search'),
      query: { query: searchQuery.value },
    });

    nextTick(() => {
      close();
    });
  }
}

function onBackspaceClick(callback, e) {
  e.preventDefault();
  callback();
  searchQuery.value = '';
  setQuery('');
  searchInput.value.focus();
}

// Expose methods to the parent component
defineExpose({
  focusSearchInput,
});
</script>

<style scoped>
@reference "~/assets/css/main.css";
.search-input::-webkit-search-decoration,
.search-input::-webkit-search-cancel-button,
.search-input::-webkit-search-results-button,
.search-input::-webkit-search-results-decoration {
  @apply hidden;
}
.search-input {
  @apply appearance-none;
}
.search-input:focus {
  @apply outline-hidden;
}

.search-input:focus + .search-button {
  @apply text-br-green;
}
</style>
